import { type FC } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { Loader } from "@jti/core-components/components/Loader";
import { type Product } from "@jti/magento";
import { isRegularGift, isStandaloneGift } from "@jti/store/utils";
import { useTranslations } from "@jti/ui";

import { CartItem } from "@checkout/components/CartItem";
import { CompactBundleItem } from "@checkout/components/CartItem/CompactBundleItem";
import { CartItemList } from "@checkout/components/CartItemList";

import styles from "./CartMiniItems.module.scss";

export type CartMiniItemsProps = {
  pending: boolean;
  items: Product[];
  buyLimitation: boolean;
  qtyPending: boolean;
  onRemoveItemClick(itemId: string): void;
  onItemQuantityChange(itemId: string, value: number): void;
  onRemoveItems(itemIds: string[]): Promise<void>;
};

export const CartMiniItems: FC<CartMiniItemsProps> = ({
  pending,
  items,
  qtyPending,
  buyLimitation,
  onRemoveItemClick,
  onItemQuantityChange,
  onRemoveItems,
}) => {
  const { __ } = useTranslations();
  const { displayBundleSelectedItems } = useConfig();

  if (pending) {
    return <Loader />;
  }

  return (
    <CartItemList
      className={styles.itemsContainer}
      emptyClassName={styles.emptyContainer}
      items={items}
      testId={"mini-cart-list"}
      emptyMessage={__("cart.cart_mini.no_items_in_cart")}
      variant={"compact"}
      renderItem={(item) => {
        if (isRegularGift(item)) return null;

        return item.type_id === "bundle" && displayBundleSelectedItems ? (
          <CompactBundleItem
            key={item.id}
            item={item}
            qtyPending={qtyPending}
            blockQuantityAdd={buyLimitation}
            onQuantityChange={onItemQuantityChange}
            onRemoveClick={onRemoveItemClick}
          />
        ) : (
          <CartItem
            key={item.id}
            item={item}
            variant="compact"
            qtyPending={qtyPending}
            blockQuantityAdd={buyLimitation}
            onRemoveClick={onRemoveItemClick}
            onQuantityChange={onItemQuantityChange}
            isRemovable={!isStandaloneGift(item)}
            isQuantityInputVisible={!isStandaloneGift(item)}
          />
        );
      }}
      onRemoveItems={onRemoveItems}
    />
  );
};
